import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Euro, Users, Send, Clock, AlertTriangle, Target, Wallet, CheckCircle2,
} from 'lucide-react';
import api from '../api';
import { PageHeader, Card, CardGridSkeleton } from '../components/ui';

const eur = (v) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v || 0);

function Stat({ icon: Icon, label, value, color, dim, onClick }) {
  return (
    <Card padding="16px 18px" style={{ cursor: onClick ? 'pointer' : 'default' }}>
      <div onClick={onClick} style={{ display: 'flex', alignItems: 'center', gap: 13 }}>
        <div style={{ width: 40, height: 40, borderRadius: 11, background: dim || 'var(--bg3)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon size={18} color={color || 'var(--text-muted)'} />
        </div>
        <div>
          <div style={{ fontSize: 23, fontWeight: 700, color: color || 'var(--text)', lineHeight: 1 }}>{value}</div>
          <div style={{ fontSize: 11.5, color: 'var(--text-dim)', marginTop: 4 }}>{label}</div>
        </div>
      </div>
    </Card>
  );
}

export default function Cockpit() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/cockpit').then(r => setD(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <CardGridSkeleton count={8} />;
  if (!d) return <div style={{ padding: 60, textAlign: 'center', color: 'var(--text-muted)' }}>Dati non disponibili.</div>;

  return (
    <div className="eden-page">
      <PageHeader title="Cockpit agenzia" icon={LayoutDashboard} subtitle="Lo stato dell'agenzia in un colpo d'occhio" />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(190px,1fr))', gap: 12, marginBottom: 16 }}>
        <Stat icon={Euro} label="MRR (contratti attivi)" value={eur(d.mrr)} color="var(--cyan)" dim="var(--cyan-dim)" onClick={() => navigate('/fatturazione')} />
        <Stat icon={Users} label="Clienti attivi" value={d.clienti_attivi} color="var(--green)" dim="var(--green-dim)" onClick={() => navigate('/clienti-attivi')} />
        <Stat icon={Send} label="Pubblicati questo mese" value={d.contenuti_pubblicati_mese} color="var(--purple)" dim="var(--purple-dim)" onClick={() => navigate('/pubblicazioni')} />
        <Stat icon={Clock} label="Approvazioni in attesa" value={d.approvazioni_attesa} color="var(--yellow)" dim="var(--yellow-dim)" />
        <Stat icon={Target} label="Lead aperti" value={`${d.lead_aperti}/${d.lead_totali}`} color="var(--text-muted)" dim="var(--bg3)" onClick={() => navigate('/leads')} />
        <Stat icon={AlertTriangle} label="Task scaduti" value={d.task_scaduti} color={d.task_scaduti > 0 ? 'var(--red)' : 'var(--green)'} dim={d.task_scaduti > 0 ? 'var(--red-dim)' : 'var(--green-dim)'} onClick={() => navigate('/scadenzario')} />
        <Stat icon={CheckCircle2} label="Incassato (mese)" value={eur(d.incassato_mese)} color="var(--green)" dim="var(--green-dim)" />
        <Stat icon={Wallet} label="Da incassare (mese)" value={eur(d.da_incassare_mese)} color="var(--yellow)" dim="var(--yellow-dim)" onClick={() => navigate('/fatturazione')} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))', gap: 16 }}>
        {/* Ultimi pubblicati */}
        <Card>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>Ultimi pubblicati</div>
          {(d.ultimi_pubblicati || []).length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--text-dim)' }}>Nessun contenuto pubblicato.</div>
          ) : (d.ultimi_pubblicati || []).map((x, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', borderBottom: i < d.ultimi_pubblicati.length - 1 ? '1px solid var(--border)' : 'none' }}>
              <span style={{ flex: 1, minWidth: 0, fontSize: 13, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{x.titolo_post}</span>
              <span style={{ fontSize: 11, color: 'var(--text-dim)', flexShrink: 0 }}>{x.cliente}</span>
            </div>
          ))}
        </Card>

        {/* Clienti a rischio */}
        <Card>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>Clienti con task scaduti</div>
          {(d.clienti_a_rischio || []).length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--green)' }}>Nessun cliente a rischio. Tutto in pari.</div>
          ) : (d.clienti_a_rischio || []).map((x, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', borderBottom: i < d.clienti_a_rischio.length - 1 ? '1px solid var(--border)' : 'none' }}>
              <AlertTriangle size={13} color="var(--red)" style={{ flexShrink: 0 }} />
              <span style={{ flex: 1, fontSize: 13, color: 'var(--text)' }}>{x.cliente}</span>
              <span style={{ fontSize: 11.5, color: 'var(--red)', fontWeight: 600 }}>{x.scaduti} scaduti</span>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}
